import { SwitchGroup, type SwitchGroupOption, type SwitchGroupProps } from './switch-group';

/** '전체' 행의 내부 sentinel — 옵션 value 와 충돌하지 않게 예약어 꼴. 밖으로 새지 않는다(커밋 값은 항상 `[]`). */
const ALL_VALUE = '__all__';

/**
 * '전체' 행을 붙이는 최소 옵션 수 — 2개 이하면 마스터 행이 목록보다 길어질 판이라 군더더기다(두 개를 켜고 끄는
 * 데 단축키가 필요하지 않다). 3개부터 "전부 되돌리기" 가 실제로 손을 아껴준다.
 */
const ALL_ROW_MIN_OPTIONS = 3;

export interface MultiSelectAllFieldProps extends SwitchGroupProps {
  /**
   * 목록 **맨 위 '전체' 행**(선택) — 주면 마스터 스위치가 붙는다. 켜면 전체(`[]`)로, 끄는 건 무시한다
   * (빈 선택 = 데이터 0 이라 사용자가 원한 상태일 리 없다 — 개별 행을 전부 꺼도 전체로 되돌린다).
   * 미지정이면 종전대로 옵션 행만 렌더한다(계측 인버터 등 기존 사용처 무변). 주더라도 **옵션이
   * `ALL_ROW_MIN_OPTIONS` 미만이면 무시**한다 — 호출처가 개수를 세지 않아도 되게 여기서 판정한다.
   */
  allOption?: { label: string; description?: string };
}

/**
 * '전체'([] = 전체) sentinel 복수선택 — SwitchGroup 위에서 빈 배열을 '전 옵션 on' 으로 표시하고, 전부 선택 시
 * 다시 [] 로 커밋(필터의 []=전체 의미). 필터 SwitchGroup 의 수작업 round-trip
 * (`value={x.length?x:opts.map} onValueChange={s=>set(s.length===opts.length?[]:s)}`)을 흡수.
 *
 * `allOption` 을 주면 맨 위에 **전체 마스터 행**을 얹는다(옵션 `ALL_ROW_MIN_OPTIONS` 개 이상일 때만) — 전 옵션이
 * 켜져 있어도 "지금이 전체 상태"라는 걸 한 줄로 읽게 해주고, 개별 선택을 하다가 한 번에 되돌릴 입구가 된다.
 */
export function MultiSelectAllField({
  options,
  value,
  onValueChange,
  allOption,
  ...rest
}: MultiSelectAllFieldProps) {
  const allValues = options.map((o) => o.value);
  const isAll = value.length === 0;
  // 표시 값 — `[]`(전체)는 전 옵션 on 으로 펼쳐 보여준다(커밋 값과 표시 값 분리).
  const shown = isAll ? allValues : value;
  const showAllRow = !!allOption && options.length >= ALL_ROW_MIN_OPTIONS;

  if (!showAllRow) {
    return (
      <SwitchGroup
        options={options}
        value={shown}
        onValueChange={(next) => onValueChange(next.length === options.length ? [] : next)}
        {...rest}
      />
    );
  }

  const rows: SwitchGroupOption[] = [
    {
      value: ALL_VALUE,
      label: allOption!.label,
      description: allOption!.description,
      // 마스터 행은 옅은 배경으로 개별 행과 위계를 가른다 — 같은 모양이면 "인버터 하나가 '전체'라는 이름으로
      // 끼어 있는" 것처럼 읽힌다. 테마 토큰(bg-muted)이라 다크에서도 따라간다.
      className: 'bg-muted/50',
    },
    ...options,
  ];

  return (
    <SwitchGroup
      options={rows}
      value={isAll ? [ALL_VALUE, ...allValues] : value}
      onValueChange={(next) => {
        const picked = new Set(next);
        // 전체 행이 토글된 경우 — 켬(부분→전체)은 전체로, 끔(전체→?)은 무시(빈 선택 금지). 개별 행 토글이면
        // 전체 행의 on/off 는 그대로라 이 분기를 타지 않고 아래 정규화로 내려간다.
        if (picked.has(ALL_VALUE) !== isAll) {
          onValueChange([]);
          return;
        }
        const selected = allValues.filter((v) => picked.has(v));
        // 전부 선택 = 전체, 전부 해제 = 전체(빈 선택 금지) — 둘 다 `[]` 로 접는다.
        onValueChange(
          selected.length === 0 || selected.length === allValues.length ? [] : selected,
        );
      }}
      {...rest}
    />
  );
}
